import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ResourceRow } from './ResourceTabCard';

/** Map-symbol visibility for the Assets tab's rows. */
export interface AssetMapSymbolControls {
  hiddenIds: ReadonlySet<string>;
  /** False when the asset has no point and no linked ArcGIS feature. */
  hasMapSymbol: (row: ResourceRow) => boolean;
  setVisible: (resourceSetId: string, visible: boolean) => void;
  setManyVisible: (resourceSetIds: string[], visible: boolean) => void;
}

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * "Show on map" checkbox for one asset. Swallows clicks, keys and pointer
 * events so the row's details dialog or the board's drag does not start.
 */
export function AssetMapSymbolCheckbox({
  row,
  controls,
}: {
  row: ResourceRow;
  controls: AssetMapSymbolControls;
}) {
  const id = row.resource_set_id;
  const name = row.name || 'asset';
  const available = !!id && controls.hasMapSymbol(row);
  const visible = available && !controls.hiddenIds.has(id!);

  const checkbox = (
    <Checkbox
      checked={visible}
      disabled={!available}
      className="tw:shrink-0"
      aria-label={visible ? `Hide ${name} on map` : `Show ${name} on map`}
      onCheckedChange={(checked) => {
        if (id) controls.setVisible(id, checked === true);
      }}
    />
  );

  return (
    <span
      className="tw:inline-flex tw:items-center"
      onClick={stop}
      onKeyDown={stop}
      onPointerDown={stop}
      onMouseDown={stop}
      onTouchStart={stop}
    >
      {available ? (
        checkbox
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={<span className="tw:inline-flex tw:cursor-not-allowed" />}
            >
              {checkbox}
            </TooltipTrigger>
            <TooltipContent>No location on the map</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
}

/** Header checkbox: show or hide every mappable asset in `rows`. */
export function AssetMapSymbolHeaderCheckbox({
  rows,
  controls,
}: {
  rows: ResourceRow[];
  controls: AssetMapSymbolControls;
}) {
  const ids = rows
    .filter((row) => row.resource_set_id && controls.hasMapSymbol(row))
    .map((row) => row.resource_set_id!);
  const shown = ids.filter((id) => !controls.hiddenIds.has(id)).length;
  const all = ids.length > 0 && shown === ids.length;

  return (
    <Checkbox
      checked={all}
      indeterminate={shown > 0 && !all}
      disabled={ids.length === 0}
      aria-label={all ? 'Hide these assets on map' : 'Show these assets on map'}
      onCheckedChange={() => controls.setManyVisible(ids, !all)}
    />
  );
}
